import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PageLayout from '../../components/layout/PageLayout';
import PageHeader from '../../components/layout/PageHeader';
import { Loading, RowsSkeleton } from '../../components/layout/Skeletons';
import { useAuth, ROLE_MODERATOR } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { useModeration } from '../../hooks/moderation/useModeration';
import { REPORT_QUEUES } from '../../constants/reportQueues';

/**
 * The reported content, whatever its kind: a post has a title and a body, a
 * comment a body, an event a title and a description, a listing a name, a
 * review a comment.
 */
function ReportItem({ report }) {
  const content = report.target.content;
  const heading = content.title || content.name;
  const text = content.body || content.description || content.comment;

  return (
    <>
      {/* No chip for the kind of content: the queue picked above already says it. */}
      <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Chip size="small" variant="outlined" label={report.reason_label} />
        {report.urgent && <Chip size="small" color="error" label="Urgent" />}
      </Stack>
      {report.comment && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          « {report.comment} » — {report.reporter}
        </Typography>
      )}
      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
        {heading && (
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {heading}
          </Typography>
        )}
        {text && (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {text}
          </Typography>
        )}
      </Paper>
    </>
  );
}

/**
 * The door: a resident who lands here is told the page is not theirs, and
 * the desk behind it — which asks the API for queues — is never mounted.
 */
function Moderation() {
  const { hasRole } = useAuth();

  if (!hasRole(ROLE_MODERATOR)) {
    return (
      <PageLayout>
        <Paper variant="outlined" sx={{ p: 5, textAlign: 'center', maxWidth: 560 }}>
          <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
            Espace réservé aux modérateurs
          </Typography>
          <Typography variant="body1">
            Les modérateurs de ville sont nommés par l’équipe du site.{' '}
            <Link component={RouterLink} to="/nous-ecrire">
              Écrivez-nous
            </Link>{' '}
            si vous souhaitez le devenir.
          </Typography>
        </Paper>
      </PageLayout>
    );
  }

  return <ModerationDesk />;
}

/**
 * A city moderator's desk: the reports filed in the cities they hold a mandate
 * for, one queue per kind of content.
 *
 * Reports are all there is to decide here. Posts and reviews are published as
 * they are written, so only what residents flag reaches a moderator; listings
 * are vetted by the back office, along with suspending an account, handing a
 * listing over and the contact messages, which an admin keeps for the whole
 * hub.
 */
function ModerationDesk() {
  const toast = useToast();
  const { user } = useAuth();
  const {
    cities,
    city,
    pending,
    mandatesLoaded,
    reportQueue,
    setReportQueue,
    setCity,
    workload,
    items,
    loading,
    error,
    reviewReport,
    requestMandate,
  } = useModeration();
  const [hiding, setHiding] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const run = async (action, success) => {
    setBusy(true);
    const message = await action();
    setBusy(false);

    if (message) {
      toast.error(message);
      return false;
    }

    toast.success(success);
    return true;
  };

  const confirmHiding = async () => {
    const done = await run(() => reviewReport(hiding, 'hide', reason.trim()), 'Contenu masqué.');

    if (done) {
      setHiding(null);
      setReason('');
    }
  };

  const livesInModeratedCity = cities.some((one) => one.slug === user?.city?.slug);
  const askedForHome = pending.some((mandate) => mandate.city.slug === user?.city?.slug);

  return (
    <PageLayout centered={false}>
      <Box sx={{ width: '100%' }}>
        <PageHeader
          title="Modération"
          lede={city ? `Les signalements à traiter à ${city.name}.` : 'Les signalements à traiter.'}
          action={
            cities.length > 1 && (
              <TextField
                select
                size="small"
                label="Ville"
                id="moderatedCity"
                value={city?.slug || ''}
                onChange={(event) => setCity(event.target.value)}
                sx={{ minWidth: 200 }}
              >
                {cities.map((one) => (
                  <MenuItem key={one.slug} value={one.slug}>
                    {one.name}
                  </MenuItem>
                ))}
              </TextField>
            )
          }
        />

        {/* A move does not carry the mandate along: the new city is asked for,
            and an admin answers. */}
        {mandatesLoaded && user?.city && !livesInModeratedCity && (
          <Alert
            severity="info"
            sx={{ mb: 3 }}
            action={
              !askedForHome && (
                <Button
                  color="inherit"
                  size="small"
                  disabled={busy}
                  onClick={() => run(requestMandate, 'Demande envoyée à l’équipe du site.')}
                >
                  Demander
                </Button>
              )
            }
          >
            {askedForHome
              ? `Votre demande pour modérer ${user.city.name} attend la réponse de l’équipe du site.`
              : `Vous habitez ${user.city.name}, que vous ne modérez pas. Vous pouvez demander à la modérer.`}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {city && (
          <>
            {/* One queue per kind of content: nothing sorts across them, so
                each says what waits in it, in red when any of it is offensive. */}
            <Stack direction="row" sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
              {REPORT_QUEUES.map((one) => {
                const counts = workload?.reports_by_type?.[one.slug];
                const waiting = counts?.pending || 0;

                return (
                  <Chip
                    key={one.slug}
                    label={waiting > 0 ? `${one.label} · ${waiting}` : one.label}
                    color={counts?.urgent > 0 ? 'error' : one.slug === reportQueue ? 'primary' : 'default'}
                    variant={one.slug === reportQueue ? 'filled' : 'outlined'}
                    onClick={() => setReportQueue(one.slug)}
                    aria-pressed={one.slug === reportQueue}
                  />
                );
              })}
            </Stack>

            {loading ? (
              <Loading label="Chargement de la file">
                <RowsSkeleton count={3} />
              </Loading>
            ) : (
              <Stack spacing={2.25}>
                {0 === items.length && (
                  <Paper variant="outlined" sx={{ p: 5, textAlign: 'center', borderStyle: 'dashed' }}>
                    <Typography variant="body1">Aucun signalement à traiter.</Typography>
                  </Paper>
                )}

                {items.map((item) => (
                  <Paper key={item.uuid} variant="outlined" sx={{ p: { xs: 2.5, md: 3 } }}>
                    <ReportItem report={item} />

                    <Stack direction="row" spacing={1} sx={{ mt: 2, justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1 }}>
                      <Button
                        variant="outlined"
                        disabled={busy}
                        onClick={() => run(() => reviewReport(item, 'dismiss'), 'Signalement classé.')}
                      >
                        Rien à signaler
                      </Button>
                      <Button variant="contained" color="error" disabled={busy} onClick={() => setHiding(item)}>
                        Masquer
                      </Button>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}
          </>
        )}

        {mandatesLoaded && !city && !error && (
          <Paper variant="outlined" sx={{ p: 5, textAlign: 'center', borderStyle: 'dashed' }}>
            <Typography variant="body1">Vous ne modérez aucune ville pour le moment.</Typography>
          </Paper>
        )}
      </Box>

      <Dialog open={Boolean(hiding)} onClose={() => setHiding(null)} fullWidth maxWidth="sm">
        <DialogTitle>Masquer ce contenu</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Le contenu disparaît du site ; son auteur voit le motif.
          </DialogContentText>
          <TextField
            label="Motif"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            multiline
            minRows={3}
            fullWidth
            autoFocus
            inputProps={{ maxLength: 500 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHiding(null)} disabled={busy} variant="outlined">
            Annuler
          </Button>
          <Button onClick={confirmHiding} color="error" variant="contained" disabled={busy || !reason.trim()}>
            Masquer
          </Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
}

export default Moderation;
